import { useSearchParams } from "react-router";
import { AlertTriangle, Store } from "lucide-react";
import type { AdminListingChangeDTO, ListingChangeStatus } from "@colab/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { cn, formatRelative } from "@/lib/utils";
import { useListingChanges } from "./use-listing-changes";
import {
  ListingChangeDrawer,
  ListingChangeStatusBadge,
} from "./listing-change-drawer";

// Marketplace listing-change review queue (0091). Partners propose edits to
// their public marketplace listing from the portal; this queue is where
// staff approve (worker writes the live marketplace row) or decline (with
// an optional note the partner sees).
//
// URL state (submissions-queue-page conventions):
//
//   ?status=pending|approved|declined   — filter chips (default pending;
//                                         param deleted at the default)
//   ?request=<uuid>                     — drawer-as-shareable-route
//
// The list endpoint always returns Colab-side rows; `configured: false`
// (MARKETPLACE_* secrets unset) leaves listing names unresolved and blocks
// approvals — surfaced by the banner and the drawer's disabled Approve.

const STATUS_OPTIONS: ReadonlyArray<{
  value: ListingChangeStatus;
  label: string;
}> = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
];

function isListingChangeStatus(value: string): value is ListingChangeStatus {
  return value === "pending" || value === "approved" || value === "declined";
}

export function ListingChangesQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusRaw = searchParams.get("status");
  const status: ListingChangeStatus =
    statusRaw && isListingChangeStatus(statusRaw) ? statusRaw : "pending";
  const selectedId = searchParams.get("request");

  const listQuery = useListingChanges(status);
  const configured = listQuery.data?.configured ?? true;
  const rows = listQuery.data?.requests ?? [];

  function updateParam(key: string, value: string | null) {
    const sp = new URLSearchParams(searchParams);
    if (value === null || value === "" || value === "pending") {
      sp.delete(key);
    } else {
      sp.set(key, value);
    }
    setSearchParams(sp, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Listing changes
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Partner-proposed edits to their public marketplace listing —
            review the diff, then approve or decline.
          </p>
        </div>
      </header>

      {listQuery.data && !configured && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Marketplace connection not configured — approvals are blocked.
          </p>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1 rounded-md border bg-card p-0.5">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateParam("status", opt.value)}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                status === opt.value
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {listQuery.isLoading ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : listQuery.isError ? (
          <EmptyState
            icon={Store}
            title="Couldn't load listing changes"
            description={listQuery.error?.message ?? "Try refreshing."}
          />
        ) : rows.length > 0 ? (
          <ListingChangesTable
            rows={rows}
            selectedId={selectedId}
            onSelect={(id) => updateParam("request", id)}
          />
        ) : (
          <EmptyState
            icon={Store}
            title={
              status === "pending"
                ? "No pending changes"
                : `No ${status} changes`
            }
            description={
              status === "pending"
                ? "When a partner submits an edit to their marketplace listing, it lands here for review."
                : `No listing changes have been ${status} yet.`
            }
          />
        )}
      </div>

      <ListingChangeDrawer
        requestId={selectedId}
        configured={configured}
        onClose={() => updateParam("request", null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table — Partner / Listing / Submitted / Status. Row click opens the drawer.
// ---------------------------------------------------------------------------

function ListingChangesTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: AdminListingChangeDTO[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Partner</th>
            <th className="px-4 py-2.5">Listing</th>
            <th className="px-4 py-2.5 text-right">Submitted</th>
            <th className="px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r.id)}
              className={cn(
                "group cursor-pointer transition-colors hover:bg-muted/30",
                selectedId === r.id && "bg-muted/30",
              )}
            >
              <td className="px-4 py-3">
                <span className="font-medium text-foreground">
                  {r.partner_name}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  {r.listing_name ?? `#${r.integration_id}`}
                  {r.stale && (
                    <span
                      className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                      title="The linked marketplace listing no longer exists"
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span className="text-[11px]">listing missing</span>
                    </span>
                  )}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="text-xs text-muted-foreground">
                  {formatRelative(r.created_at)}
                </span>
              </td>
              <td className="px-4 py-3">
                <ListingChangeStatusBadge status={r.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
